// Stempeluhr in der Pille (Handoff Pille §5, Screen 13) — große Uhr mit Kommen / Pause↔Weiter /
// Gehen, Heute/Soll/Saldo, Tagesereignisse und Wochenbalken aus den echten Stempeln.
// Ab 700 px zwei Spalten, darunter eine.
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CalendarBlank, CaretRight, Coffee, Play, SignIn, SignOut, SpinnerGap, Stop, WarningCircle, X, type Icon } from '@phosphor-icons/react';
import { useZeit, laufendSeit } from '../../../contexts/ZeitContext';
import { stundenText, tagVon, uhrzeit } from '../../../lib/zeiterfassung/berechnung';
import { stempelFehlerText, tagesereignisse, wochenBalken, zeitRest, type EreignisArt } from '../../../lib/island/zeitApp';
import { zeitRing } from '../../../lib/island/homescreen';
import { useIslandActions } from '../IslandProvider';
import { useViewport } from '../hooks';
import { FARBE } from '../pille-stil';

const KARTE: CSSProperties = {
  borderRadius: 24, padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
  background: 'linear-gradient(160deg, rgba(255,255,255,0.09), rgba(255,255,255,0.035))',
  boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.2), inset 0 0 0 0.5px rgba(255,255,255,0.07)',
};
const KOPF: CSSProperties = { fontSize: 13, fontWeight: 700, color: FARBE.text2 };

const EREIGNIS: Record<EreignisArt, { label: string; icon: Icon; farbe: string }> = {
  kommen: { label: 'Kommen', icon: SignIn, farbe: FARBE.gruen },
  pause: { label: 'Pause', icon: Coffee, farbe: '#ffb35c' },
  weiter: { label: 'Weiter', icon: Play, farbe: '#64d2ff' },
  gehen: { label: 'Gehen', icon: SignOut, farbe: FARBE.rotHell },
};

const RING_U = 2 * Math.PI * 46;

export function ZeitPage() {
  const {
    laufend, inPause, jetzt, laufendeMinuten, heuteMinuten, heuteSollMinuten, saldoMinuten, heuteStempel, woche,
    bereit, starten, beenden, pauseUmschalten,
  } = useZeit();
  const { collapse, onSearchNavigate } = useIslandActions();
  const ruhig = useReducedMotion() ?? false;
  const zweiSpalten = useViewport().width >= 700;
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const fehlerRef = useRef<HTMLDivElement>(null);
  // Das Zeit-Deck ist niedrig – der Hinweis soll nicht unter der Kante verschwinden.
  useEffect(() => { if (fehler) fehlerRef.current?.scrollIntoView({ block: 'nearest', behavior: ruhig ? 'auto' : 'smooth' }); }, [fehler, ruhig]);

  /** Stempel-Aktion ausführen; scheitert das Speichern, bleibt ein Hinweis stehen. */
  async function tue(fn: () => Promise<boolean>, art: EreignisArt) {
    setArbeitet(true);
    setFehler(null);
    let ok = false;
    try { ok = await fn(); } catch { ok = false; } finally { setArbeitet(false); }
    if (!ok) setFehler(stempelFehlerText(art));
  }

  if (!bereit) {
    return (
      <div className="flex h-full items-center justify-center" style={{ color: 'rgba(235,235,245,0.4)' }}>
        <SpinnerGap size={20} className="pille-dreh" aria-label="Lädt" />
      </div>
    );
  }

  const ring = zeitRing(heuteMinuten, heuteSollMinuten);
  const rest = zeitRest(heuteMinuten, heuteSollMinuten);
  const status = laufend ? (inPause ? 'Pause läuft' : 'Arbeitszeit läuft') : 'Heute gearbeitet';
  const ereignisse = tagesereignisse(heuteStempel);
  const balken = wochenBalken(woche, tagVon(jetzt.toISOString()));
  const wocheIst = woche.reduce((s, z) => s + z.istMinuten, 0);
  const wocheSoll = woche.reduce((s, z) => s + z.sollMinuten, 0);

  const grossKnopf: CSSProperties = {
    flex: 1, height: 54, borderRadius: 18, border: 'none', gap: 8, fontFamily: 'inherit', fontSize: 15, fontWeight: 800, cursor: 'pointer',
    opacity: arbeitet ? 0.6 : 1,
  };

  return (
    <div className="grid h-full content-start overflow-y-auto text-white" style={{ padding: '4px 16px 18px', gap: 14, gridTemplateColumns: zweiSpalten ? 'minmax(0,1fr) minmax(0,1fr)' : 'minmax(0,1fr)' }}>
      {/* Große Uhr + Knöpfe */}
      <div data-bj="zeit" style={{ ...KARTE, alignSelf: 'start', alignItems: 'center', gap: 16, padding: '20px 16px', background: `radial-gradient(90% 70% at 50% 0%, rgba(255,159,10,0.22), transparent 70%), ${KARTE.background as string}` }}>
        <div className="relative" style={{ width: 196, height: 196 }}>
          <svg width="196" height="196" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }} aria-hidden>
            <defs>
              <linearGradient id="pille-zeituhr" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#ffe0a3" /><stop offset="1" stopColor="#ff8a00" /></linearGradient>
            </defs>
            <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
            <motion.circle cx="50" cy="50" r="46" fill="none" stroke="url(#pille-zeituhr)" strokeWidth="7" strokeLinecap="round"
              strokeDasharray={RING_U}
              initial={ruhig ? false : { strokeDashoffset: RING_U }}
              animate={{ strokeDashoffset: RING_U * (1 - ring.anteil) }}
              transition={{ duration: 1.2, delay: 0.15, ease: [0.32, 0.72, 0, 1] }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ gap: 4 }}>
            <span className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'rgba(235,235,245,0.7)' }}>
              <span className={laufend && !inPause ? 'pille-puls-punkt' : undefined} aria-hidden
                style={{ width: 7, height: 7, borderRadius: '50%', background: laufend ? (inPause ? '#ffb35c' : FARBE.gruen) : 'rgba(235,235,245,0.4)' }} />
              {status}
            </span>
            <span className="tabular-nums" style={{ fontSize: 46, fontWeight: 800, letterSpacing: '-0.045em', lineHeight: 1, color: laufend && !inPause ? FARBE.gold : '#fff' }}>
              {stundenText(laufend ? laufendeMinuten : heuteMinuten).replace(' h', '')}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: rest.ueber ? FARBE.gruen : 'rgba(235,235,245,0.55)' }}>{rest.text}</span>
          </div>
        </div>
        {laufend && <span style={{ marginTop: -6, fontSize: 12, fontWeight: 600, color: FARBE.text2 }}>{laufendSeit(laufend, inPause)}</span>}
        <div className="flex w-full" style={{ gap: 8 }}>
          {!laufend ? (
            <button type="button" disabled={arbeitet} onClick={() => void tue(starten, 'kommen')} className="pille-gross-knopf flex items-center justify-center"
              style={{ ...grossKnopf, background: 'linear-gradient(180deg,#4ae07a,#1fae4c)', color: '#04210d', boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.5), 0 12px 24px -12px rgba(48,209,88,0.8)' }}>
              {arbeitet ? <SpinnerGap size={18} className="pille-dreh" /> : <Play size={18} weight="fill" />} Kommen
            </button>
          ) : (
            <>
              <button type="button" disabled={arbeitet} onClick={() => void tue(pauseUmschalten, inPause ? 'weiter' : 'pause')} className="pille-gross-knopf flex items-center justify-center"
                style={{ ...grossKnopf, background: inPause ? 'linear-gradient(160deg,#f1dcb4,#d4ad73 55%,#9c7440)' : 'rgba(255,255,255,0.12)', color: inPause ? '#231806' : '#fff', boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.25)' }}>
                <Coffee size={18} weight="fill" /> {inPause ? 'Weiter' : 'Pause'}
              </button>
              <button type="button" disabled={arbeitet} onClick={() => void tue(beenden, 'gehen')} className="pille-gross-knopf flex items-center justify-center"
                style={{ ...grossKnopf, background: 'linear-gradient(180deg,#ff7a70,#e0352b)', color: '#fff', boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.4), 0 12px 24px -12px rgba(255,69,58,0.8)' }}>
                {arbeitet ? <SpinnerGap size={18} className="pille-dreh" /> : <Stop size={18} weight="fill" />} Gehen
              </button>
            </>
          )}
        </div>
        {fehler && (
          <div ref={fehlerRef} role="alert" className="flex w-full items-start" style={{ gap: 8, padding: '8px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: 'rgba(255,69,58,0.14)', color: '#ff8a80' }}>
            <WarningCircle size={15} weight="fill" className="shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">{fehler}</span>
            <button type="button" onClick={() => setFehler(null)} aria-label="Hinweis schließen" className="shrink-0 cursor-pointer"
              style={{ border: 'none', background: 'transparent', color: 'inherit', padding: 0 }}>
              <X size={12} weight="bold" />
            </button>
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-col" style={{ gap: 14 }}>
        {/* Heute / Soll / Saldo */}
        <div className="grid grid-cols-3" style={{ gap: 10 }}>
          {[
            { label: 'Heute', wert: stundenText(heuteMinuten), farbe: '#fff' },
            { label: 'Soll', wert: stundenText(heuteSollMinuten), farbe: '#fff' },
            { label: 'Saldo', wert: stundenText(saldoMinuten, true), farbe: saldoMinuten > 0 ? '#5fd38a' : saldoMinuten < 0 ? FARBE.orange : '#fff' },
          ].map(k => (
            <div key={k.label} style={{ ...KARTE, gap: 3, padding: 13 }}>
              <span style={{ ...KOPF, fontSize: 12 }}>{k.label}</span>
              <span className="whitespace-nowrap tabular-nums" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', color: k.farbe }}>{k.wert}</span>
            </div>
          ))}
        </div>

        {/* Tagesereignisse */}
        {ereignisse.length > 0 && (
          <div style={KARTE}>
            <span style={KOPF}>Heute</span>
            <ol className="flex flex-col" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {ereignisse.map((e, i) => {
                const d = EREIGNIS[e.art];
                const I = d.icon;
                return (
                  <li key={`${e.art}-${e.zeit}`} className="flex" style={{ gap: 12 }}>
                    <div className="flex flex-col items-center">
                      <span className="flex shrink-0 items-center justify-center rounded-full" style={{ width: 28, height: 28, background: 'rgba(255,255,255,0.08)' }} aria-hidden>
                        <I size={14} weight="fill" color={d.farbe} />
                      </span>
                      {i < ereignisse.length - 1 && <span aria-hidden style={{ flex: 1, width: 2, minHeight: 10, background: 'rgba(255,255,255,0.1)' }} />}
                    </div>
                    <div className="flex flex-1 items-center justify-between" style={{ padding: '4px 0 12px' }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{d.label}</span>
                      <span className="tabular-nums" style={{ fontSize: 14, fontWeight: 700, color: FARBE.text2 }}>{uhrzeit(e.zeit)}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {/* Diese Woche */}
        <div style={KARTE}>
          <div className="flex items-baseline justify-between">
            <span style={KOPF}>Diese Woche</span>
            <span className="tabular-nums" style={{ fontSize: 12, fontWeight: 700, color: 'rgba(235,235,245,0.55)' }}>{stundenText(wocheIst)} / {stundenText(wocheSoll)}</span>
          </div>
          <div className="flex items-end" style={{ gap: 10, height: 110 }} role="img"
            aria-label={`Diese Woche: ${balken.map(b => `${b.kuerzel} ${b.wert === '–' ? 'keine Zeit' : `${b.wert} h`}`).join(', ')}`}>
            {balken.map(b => {
              const heute = b.status === 'heute';
              return (
                <div key={b.tag} className="flex h-full flex-1 flex-col items-center justify-end" style={{ gap: 5 }} aria-hidden>
                  <span className="tabular-nums" style={{ fontSize: 10, fontWeight: heute ? 800 : 600, color: heute ? FARBE.gold : 'rgba(235,235,245,0.55)' }}>{b.wert}</span>
                  <div className="flex min-h-0 w-full flex-1 items-end" style={{ maxWidth: 34 }}>
                    <motion.div style={{ width: '100%', borderRadius: 9, background: heute ? 'linear-gradient(180deg,#ffd27a,#d4ad73)' : b.status === 'vergangen' ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.08)' }}
                      initial={ruhig ? false : { height: '4%' }} animate={{ height: `${b.hoehe}%` }} transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }} />
                  </div>
                  <span style={{ fontSize: 11, fontWeight: heute ? 800 : 600, color: heute ? FARBE.gold : 'rgba(235,235,245,0.55)' }}>{b.kuerzel}</span>
                </div>
              );
            })}
          </div>
        </div>

        <button type="button" onClick={() => { collapse(); onSearchNavigate?.('/admin/zeiterfassung', '', ''); }}
          className="pille-zeile flex cursor-pointer items-center justify-between"
          style={{ height: 46, borderRadius: 16, border: 'none', padding: '0 16px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: '#fff' }}>
          <span className="flex items-center" style={{ gap: 10 }}><CalendarBlank size={17} weight="fill" color={FARBE.gold} aria-hidden />Monatsübersicht öffnen</span>
          <CaretRight size={13} weight="bold" color="rgba(235,235,245,0.4)" aria-hidden />
        </button>
      </div>
    </div>
  );
}
